import React, { useState } from 'react';
import { Button, Card, Alert, ErrorAlert } from '../../../components/shared';
import { cn } from '../../../utils/cn';

const FIELD_LABELS = {
  cardType: 'Loại giấy tờ',
  fullName: 'Họ và tên',
  idNumber: 'Số CCCD',
  dateOfBirth: 'Ngày sinh',
  gender: 'Giới tính',
  nationality: 'Quốc tịch',
  ethnicity: 'Dân tộc',
  issueDate: 'Ngày cấp',
  expiryDate: 'Có giá trị đến',
  issuePlace: 'Nơi cấp',
  originAddress: 'Quê quán',
  permanentAddress: 'Nơi ĐKHK TT',
};

const GROUP_LABELS = {
  document: 'Giấy tờ',
  face: 'Khuôn mặt',
};

const TABS = [
  { key: 'info', label: 'Thông tin cá nhân' },
  { key: 'validate', label: 'Xác thực' },
  { key: 'debug', label: 'Debug' },
];

function InfoTab({ extractedData, validations }) {
  const docWarnings = validations
    ?.filter((v) => v.group === 'document' && v.passed === false)
    ?.flatMap((v) => {
      if (v.key === 'field_warnings' && v.value) return v.value.split(', ');
      return [`${v.label}: ${v.value}`];
    }) || [];

  const faceItem = validations?.find((v) => v.key === 'face_match');

  return (
    <div className="divide-y divide-gray-100">
      {docWarnings.length > 0 && (
        <div className="flex items-start justify-between py-3">
          <span className="text-sm text-gray-500">Giấy tờ</span>
          <div className="text-right">
            {docWarnings.map((msg, i) => (
              <p key={i} className="text-sm font-medium text-red-600">{msg}</p>
            ))}
          </div>
        </div>
      )}
      {Object.entries(FIELD_LABELS).map(([key, label]) => {
        const value = extractedData[key];
        return (
          <div key={key} className="flex items-start justify-between py-3">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-right text-sm font-medium text-gray-900">{value || '-'}</span>
          </div>
        );
      })}
      {extractedData.address && (
        <div className="flex items-start justify-between py-3">
          <span className="text-sm text-gray-500">Địa chỉ</span>
          <div className="text-right text-sm text-gray-900">
            {[extractedData.address.ward, extractedData.address.district, extractedData.address.province]
              .filter(Boolean)
              .map((item) => item.text)
              .filter(Boolean)
              .join(', ') || '-'}
          </div>
        </div>
      )}
      {faceItem && (
        <div className="flex items-center justify-between py-3">
          <span className="text-sm text-gray-500">So sánh</span>
          <span className={cn(
            'text-sm font-medium',
            faceItem.passed ? 'text-green-600' : 'text-red-600'
          )}>
            {faceItem.passed ? 'Khuôn mặt khớp' : 'Khuôn mặt không khớp'}
          </span>
        </div>
      )}
    </div>
  );
}

function ValidateTab({ validations }) {
  if (!validations || validations.length === 0) {
    return <p className="py-4 text-center text-sm text-gray-400">Chưa có dữ liệu xác thực</p>;
  }

  const groups = {};
  validations.forEach((item) => {
    const g = item.group || 'other';
    if (!groups[g]) groups[g] = [];
    groups[g].push(item);
  });

  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([group, items]) => {
        const allPassed = items.every((i) => i.passed !== false);
        return (
          <div key={group}>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">
                {GROUP_LABELS[group] || group}
              </span>
              <span className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                allPassed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              )}>
                {allPassed ? 'Hợp lệ' : 'Không hợp lệ'}
              </span>
            </div>
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {items.map((item) => (
                <div key={item.key} className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-sm text-gray-500">{item.label}</span>
                  <span className={cn(
                    'text-sm font-medium',
                    item.passed === true ? 'text-green-600' :
                    item.passed === false ? 'text-red-600' : 'text-gray-900'
                  )}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DebugTab({ rawResponse }) {
  if (!rawResponse) {
    return <p className="py-4 text-center text-sm text-gray-400">Debug mode chưa được bật trên server</p>;
  }

  return (
    <pre className="max-h-96 overflow-auto rounded-lg bg-gray-900 p-4 text-xs text-green-400">
      {JSON.stringify(rawResponse, null, 2)}
    </pre>
  );
}

export default function ConfirmStep({ extractedData, validations, rawResponse, onConfirm, loading, error }) {
  const [activeTab, setActiveTab] = useState('info');

  const availableTabs = TABS.filter((t) => t.key !== 'debug' || rawResponse);

  if (!extractedData) {
    return (
      <Card title="Xác nhận thông tin">
        <Alert type="warning">Chưa có dữ liệu bóc tách. Vui lòng thực hiện lại bước OCR.</Alert>
      </Card>
    );
  }

  return (
    <Card title="Xác nhận thông tin" description="Kiểm tra thông tin được bóc tách từ giấy tờ">
      <ErrorAlert error={error} className="mb-4" />

      <div className="mb-4 flex border-b border-gray-200">
        {availableTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.key
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'info' && <InfoTab extractedData={extractedData} validations={validations} />}
      {activeTab === 'validate' && <ValidateTab validations={validations} />}
      {activeTab === 'debug' && <DebugTab rawResponse={rawResponse} />}

      <div className="mt-6 flex justify-end">
        <Button onClick={onConfirm} loading={loading}>
          Xác nhận và hoàn tất
        </Button>
      </div>
    </Card>
  );
}
